/**
 * The single place the SPA talks to the API.
 *
 * `credentials: 'include'` because the session is an httpOnly cookie the JavaScript
 * cannot read (ADR-0017), and `X-Tard-Request` because the API's CSRF guard requires a
 * header no cross-site form post can set. Both belong here rather than at call sites:
 * one forgotten option is a request that silently drops the session.
 */

export interface ApiError {
  readonly error: string;
  /** `error.<code>` — the i18n key. The API always sends it. */
  readonly messageKey: string;
  readonly message: string;
  readonly details?: readonly { readonly path: string; readonly code: string }[];
  /** Quotable in a support ticket; ties the response to a log line. */
  readonly requestId?: string;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.message);
    this.name = 'ApiRequestError';
  }
}

const BASE = '/api/v1';

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Tard-Request': 'spa',
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });

  const text = await response.text();

  /**
   * Parse defensively.
   *
   * An unguarded `JSON.parse` throws a bare `SyntaxError` the moment anything upstream
   * answers with HTML — a proxy error page, a misrouted request — and that escapes the
   * error contract entirely: no code, no `messageKey`, nothing a caller can branch on.
   * A body we cannot read is reported as a failure like any other.
   */
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiRequestError(response.status, {
        error: 'unreadable_response',
        messageKey: 'error.unreadable_response',
        message: `The server returned a response that was not JSON (HTTP ${response.status}).`,
      });
    }
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status, asApiError(parsed, response.status));
  }

  return parsed as T;
}

/**
 * The same request, for a response that is **not** JSON.
 *
 * The evidence bytes route returns a sealed proof artifact as an attachment (ADR-0056), and
 * `apiFetch` would refuse it: it reads the body as text and throws `unreadable_response` the
 * moment `JSON.parse` fails. So this is a sibling rather than a flag on the original —
 * `apiFetch`'s contract is "a JSON body or an `ApiRequestError`", and widening it to sometimes
 * return a Blob would make every caller's return type a union it does not want.
 *
 * **Failures still come back as JSON**, because the API's exception filter answers with the
 * error envelope whatever the route's success content type is — verified against a running
 * server, since a decorator-pinned content type that leaked onto errors would make every
 * refusal unreadable. So the error path here parses text exactly as `apiFetch` does.
 */
export async function apiFetchBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'X-Tard-Request': 'spa', ...init.headers },
  });

  if (!response.ok) {
    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = text === '' ? null : JSON.parse(text);
    } catch {
      parsed = null;
    }
    throw new ApiRequestError(response.status, asApiError(parsed, response.status));
  }

  return response.blob();
}

function asApiError(parsed: unknown, status: number): ApiError {
  if (parsed !== null && typeof parsed === 'object' && 'error' in parsed && 'message' in parsed) {
    return parsed as ApiError;
  }
  return {
    error: 'unexpected',
    messageKey: 'error.unexpected',
    message: `HTTP ${status}`,
  };
}

export interface HealthView {
  readonly ok: boolean;
  readonly db: 'up' | 'down';
  /** Null for an anonymous caller — queue depth tracks order volume. */
  readonly outboxPending: number | null;
  /** Narrowed from the API's `string` so the login page can branch exhaustively. */
  readonly identityProvider: 'fake' | 'ikigai';
}

export function getHealth(): Promise<HealthView> {
  return apiFetch<HealthView>('/health');
}

export type PrincipalRole = 'merchantUser' | 'opsUser';

export interface TenantView {
  readonly id: string;
  readonly displayNameAr: string;
  readonly displayNameEn: string | null;
}

/**
 * A discriminated union rather than optional fields.
 *
 * The API's own type uses `principalId?: string`, but `exactOptionalPropertyTypes` makes
 * that awkward to consume — and more to the point, "authenticated with no principal id" is
 * not a state that exists. Modelling it as a union means the compiler stops a component
 * reading a tenant off an anonymous session.
 */
export type SessionView =
  | { readonly authenticated: false }
  | {
      readonly authenticated: true;
      readonly principalId: string;
      readonly role: PrincipalRole;
      readonly platformStaff: boolean;
      /**
       * Which facets of Ops this session holds (ADR-0060).
       *
       * Here so a screen can hide what its viewer cannot reach. Before M9 the console had no
       * way to ask: `ops-compliance.tsx` carried a comment saying the facet it needs "the
       * console cannot grant", and an operator without it met a 403 on a page that had
       * offered itself. Empty for every Merchant user and for most Ops users.
       */
      readonly opsFacets: readonly string[];
      readonly label: string | null;
      readonly tenant: TenantView | null;
      /**
       * Invitations waiting for this person's answer (ADR-0067, amended). `awaitingInvitation` is
       * true when one of them is the only access they have: until they accept it, nothing else
       * answers them. Optional so an older API's envelope still parses.
       */
      readonly invitations?: readonly InvitationView[];
      readonly awaitingInvitation?: boolean;
    };

/** One invitation, as the person it names sees it: no email, theirs or the inviter's. */
export interface InvitationView {
  readonly invitationId: string;
  readonly label: string | null;
  readonly facets: readonly string[];
  readonly invitedBy: string | null;
  /** `own`: it is this person's only access. `offer`: accepting adds its areas to their account. */
  readonly kind: 'own' | 'offer';
}

/**
 * The bootstrap probe.
 *
 * `@Public()` on the API side and always 200 — `{authenticated: false}` when anonymous,
 * never a 401. That is why this and not `/me` is what the app calls on first paint: a 401
 * during boot is indistinguishable from a broken API, and would make "signed out" and
 * "server down" render the same.
 */
export function getSession(): Promise<SessionView> {
  return apiFetch<SessionView>('/auth/session');
}

export interface LogoutResult {
  readonly loggedOut: true;
  /**
   * Ikigai has no logout endpoint, so its session survives ours.
   *
   * The API returns this so the UI can say so. A "log out" that quietly does half of what
   * the user expects is worse than one labelled honestly (ADR-0017).
   */
  readonly ikigaiSessionRemains: boolean;
}

export function logout(): Promise<LogoutResult> {
  return apiFetch<LogoutResult>('/auth/logout', { method: 'POST' });
}

export interface FakeLoginResult {
  readonly principalId: string;
  readonly role: string;
  readonly tenantId: string | null;
}

/** Development sign-in. The route does not exist when `identityProvider` is `ikigai`. */
export function fakeLogin(email: string): Promise<FakeLoginResult> {
  return apiFetch<FakeLoginResult>('/auth/fake/login', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}
